/**
 * <s-product-gallery> — Main product image with prev/next arrows and a thumbnail strip.
 *
 * Commands (via commandfor/command on descendant buttons):
 *   --prev  — Scroll to the previous image (wraps).
 *   --next  — Scroll to the next image (wraps).
 *   --goto  — Scroll to the image whose index is the source button's data-index.
 *
 * Required child markup:
 *   [data-gallery-track] — Scroll-snap track.
 *   [data-gallery-slide] — Each full-width slide.
 *   [data-gallery-thumb] — Thumbnail buttons with data-index; aria-current marks the active one.
 */
class SProductGallery extends HTMLElement {
  #ac;
  #track;
  #slides = [];
  #thumbs = [];
  #current = 0;
  #observer;
  #variantMedia = {};

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    this.#track = this.querySelector('[data-gallery-track]');
    if (!this.#track) return;
    this.#slides = Array.from(this.#track.querySelectorAll('[data-gallery-slide]'));
    this.#thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));

    this.addEventListener('command', (event) => {
      const total = this.#slides.length;
      if (!total) return;
      if (event.command === '--next') this.goTo((this.#current + 1) % total);
      else if (event.command === '--prev') this.goTo((this.#current - 1 + total) % total);
      else if (event.command === '--goto') {
        const index = Number(event.source?.dataset.index);
        if (Number.isInteger(index)) this.goTo(index);
      }
    }, opts);

    this.#observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) this.#setCurrent(this.#slides.indexOf(entry.target));
      }
    }, { root: this.#track, threshold: 0.6 });
    for (const slide of this.#slides) this.#observer.observe(slide);
    this.#setCurrent(0);

    // Variant → image: jump to the chosen finish's photo as soon as a variant is picked.
    // data-variant-media maps variant id → media id; slides carry data-media-id.
    try {
      this.#variantMedia = JSON.parse(this.dataset.variantMedia || '{}');
    } catch {
      this.#variantMedia = {};
    }
    document.addEventListener('change', (event) => {
      const input = event.target;
      const variantId = input?.dataset?.variantId || input?.selectedOptions?.[0]?.dataset?.variantId;
      if (variantId) this.showVariant(variantId);
    }, opts);

    // After a re-render (or on a ?variant= link), land on the selected finish's photo.
    requestAnimationFrame(() => {
      if (!this.isConnected || !this.#track) return;
      const urlVariant = new URL(location.href).searchParams.get('variant');
      if (!(urlVariant && this.showVariant(urlVariant, true))) {
        this.#track.scrollTo({ left: 0, behavior: 'instant' });
        this.#setCurrent(0);
      }
    });
  }

  /** Scrolls to the slide for a variant's image. Returns true when one was found. */
  showVariant(variantId, instant = false) {
    const mediaId = this.#variantMedia[String(variantId)];
    if (!mediaId) return false;
    const index = this.#slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
    if (index < 0) return false;
    if (instant) {
      const slide = this.#slides[index];
      this.#track.scrollTo({ left: slide.offsetLeft - this.#track.offsetLeft, behavior: 'instant' });
      this.#setCurrent(index);
    } else {
      this.goTo(index);
    }
    return true;
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
  }

  goTo(index) {
    const slide = this.#slides[index];
    if (!slide) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.#track.scrollTo({ left: slide.offsetLeft - this.#track.offsetLeft, behavior: reduce ? 'auto' : 'smooth' });
    this.#setCurrent(index);
  }

  #setCurrent(index) {
    if (index < 0) return;
    this.#current = index;
    this.#thumbs.forEach((thumb, i) => {
      if (i === index) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    });
  }
}

if (!customElements.get('s-product-gallery')) customElements.define('s-product-gallery', SProductGallery);
